import React from 'react';
import {
  Box, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import { ReceiptLongOutlined } from '@mui/icons-material';
import { AccountLedgerResponse } from '../../../types/finance';
import { useTimezone } from '../../../hooks/useTimezone';
import { formatDrCr } from '../../../utils/paisa';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import EmptyState from '../../common/EmptyState';
import Amount from './Amount';
import AccountLabel from './AccountLabel';
import MovementDescription from './MovementDescription';

const COLUMNS = 7;

/**
 * An account's lines in date order: debit, credit and the balance after each,
 * between an opening row (first page) and a closing row (last page). A row
 * opens its voucher.
 */
const AccountLedgerTable: React.FC<{
  data: AccountLedgerResponse | null;
  loading: boolean;
  error: string | null;
  filtered: boolean;
  page: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onOpen: (entryId: string) => void;
}> = ({ data, loading, error, filtered, page, limit, onPage, onLimit, onOpen }) => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const items = data?.items ?? [];
  const isGroup = !!data?.account.isGroup;
  const lastPage = !data || page >= data.pagination.totalPages - 1;

  const balanceRow = (label: string, paisa: number) => (
    <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.06), '& td': { borderBottomColor: alpha(theme.palette.warning.main, 0.25) } }}>
      <TableCell colSpan={COLUMNS - 1}>
        <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDrCr(paisa)}</Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
      {loading && items.length > 0 && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell sx={{ width: 110 }}>Date</TableCell>
              <TableCell sx={{ width: 160 }}>Voucher</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ width: 200 }}>{isGroup ? 'Account' : 'Against'}</TableCell>
              <TableCell sx={{ width: 120 }} align="right">Debit</TableCell>
              <TableCell sx={{ width: 120 }} align="right">Credit</TableCell>
              <TableCell sx={{ width: 150 }} align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && page === 0 && balanceRow('Opening balance', data.openingPaisa)}
            {loading && items.length === 0 ? <TableRowSkeletons columns={COLUMNS} /> : items.map(l => {
              const other = isGroup ? (l.account ? [l.account] : []) : l.against;
              return (
                <TableRow key={l.lineId} hover onClick={() => onOpen(l.entryId)} sx={{ cursor: 'pointer' }}>
                  <TableCell><Typography variant="body2" noWrap>{formatDate(l.entryDate)}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: 'monospace' }}>{l.entryNo}</Typography></TableCell>
                  <TableCell>
                    <MovementDescription text={l.description || l.memo} status={l.status} isReversal={l.isReversal} entryId={l.entryId} createdByName={l.createdByName} />
                  </TableCell>
                  <TableCell>
                    {other.length === 0 ? <Typography variant="body2" color="text.disabled">—</Typography> : (
                      <Tooltip title={other.map(a => `${a.code} ${a.name}`).join(', ')} disableInteractive>
                        <Box sx={{ maxWidth: 200 }}>
                          <AccountLabel code={other[0].code} name={other[0].name} />
                          {other.length > 1 && <Typography variant="caption" color="text.secondary" display="block">and {other.length - 1} more</Typography>}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right"><Amount paisa={l.debitPaisa} fontWeight={600} /></TableCell>
                  <TableCell align="right"><Amount paisa={l.creditPaisa} fontWeight={600} /></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDrCr(l.balancePaisa)}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {data && lastPage && items.length > 0 && balanceRow('Closing balance', data.closingPaisa)}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && items.length === 0 && (
        <Box sx={{ p: 3 }}>
          <EmptyState
            icon={<ReceiptLongOutlined />}
            title={error ? 'The ledger could not load' : filtered ? 'No lines match' : 'Nothing posted in these dates'}
            description={error || (filtered ? 'Clear a filter to see more.' : 'Choose wider dates to see this account\'s history.')}
            dense
          />
        </Box>
      )}

      <TablePagination
        component="div"
        count={data?.pagination.totalItems ?? 0}
        page={page}
        onPageChange={(_, p) => onPage(p)}
        rowsPerPage={limit}
        onRowsPerPageChange={e => onLimit(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Paper>
  );
};

export default AccountLedgerTable;
